import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_KEY = 'taskmaster_session_token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem(SESSION_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback component will handle the session_id exchange.
    if (window.location.hash?.includes('session_id=') || window.__TASKMASTER_PENDING_SESSION) {
      setLoading(false);
      return;
    }

    // Normal auth check: verify existing session via Bearer token
    const headers = getAuthHeaders();
    if (!headers.Authorization) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, { headers });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem(SESSION_KEY);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    window.location.href = '/';
  };

  const exchangeSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      const data = response.data;
      if (data.session_token) {
        localStorage.setItem(SESSION_KEY, data.session_token);
      }
      setUser(data);
      return data;
    } catch (error) {
      console.error('Session exchange error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, exchangeSession, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
